import React from "react";
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  InputBase,
  List,
  Paper,
  Popper,
  useMediaQuery,
} from "@mui/material";
import { FolderItem } from "global/components/Folders/types";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";
import SearchResultRow from "global/components/SearchResultRow/SearchResultRow";
import strings from "global/constants/StringConstants";
import { DateRange } from "models/interfaces";
import signRequestsStyles from "../SignRequests.styles";
import { signRequestsResponsiveTokens } from "../SignRequests.tokens";
import {
  CalendarGlyph,
  CaretDownGlyph,
  FilterLinesGlyph,
  SearchGlyph,
} from "./SignRequestsIcons";
import EmptyState from "global/components/EmptyState/EmptyState";

const styles = signRequestsStyles;
const { phone } = signRequestsResponsiveTokens;

const SEARCH_PLACEHOLDER = "Search requests by name, recipient, or status";

interface SignRequestsToolbarProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  searchAnchorRef: React.MutableRefObject<HTMLDivElement | null>;
  searchOpen: boolean;
  setSearchOpen: (value: boolean) => void;
  isSearching: boolean;
  searchResults: FolderItem[];
  resolveAncestorCrumbs: (item: FolderItem) => string[];
  onSearchSelect: (item: FolderItem) => void;
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  openDateRangePicker: boolean;
  setOpenDateRangePicker: (value: boolean) => void;
  onDateApplied: Function;
  disabled: boolean;
  /** Optional trailing controls per result row (the "more options" menu). */
  renderItemActions?: (item: FolderItem) => React.ReactNode;
  /** Optional inline marker per result row (a request's status chip). */
  renderItemBadge?: (item: FolderItem) => React.ReactNode;
}

/**
 * Search field plus date-range chip that sit above the filter tabs. Both are
 * drawn from the screen tokens; the date control re-skins the shared
 * `CustomDateRangePicker` through its `sx` / `startIcon` / `endIcon` props
 * rather than duplicating the picker.
 */
const SignRequestsToolbar = ({
  searchText,
  onSearchChange,
  searchAnchorRef,
  searchOpen,
  setSearchOpen,
  isSearching,
  searchResults,
  resolveAncestorCrumbs,
  onSearchSelect,
  dateRange,
  setDateRange,
  openDateRangePicker,
  setOpenDateRangePicker,
  onDateApplied,
  disabled,
  renderItemActions,
  renderItemBadge,
}: SignRequestsToolbarProps) => {
  /*
   * A `placeholder` is an attribute, not a styled node, so the phone's shorter
   * prompt needs a media-query hook rather than a breakpoint value. Everything
   * else in this toolbar switches through `sx`.
   */
  const isPhone = useMediaQuery("@media (max-width:599.95px)");
  const placeholder = isPhone
    ? phone.search.placeholderShort
    : SEARCH_PLACEHOLDER;

  return (
    <Box sx={styles.toolbar}>
      <Box ref={searchAnchorRef} sx={styles.searchField}>
        <SearchGlyph sx={styles.searchGlyph} />
        <InputBase
          id="requests_search_box"
          type="search"
          placeholder={placeholder}
          value={searchText}
          onChange={(event) => onSearchChange(event.target.value)}
          disabled={disabled}
          sx={styles.searchInput}
          inputProps={{ "aria-label": SEARCH_PLACEHOLDER }}
        />
        {isSearching && (
          <CircularProgress
            size={16}
            role="status"
            aria-label="Searching requests"
          />
        )}
      </Box>

      <Popper
        open={searchOpen && !!searchAnchorRef.current}
        anchorEl={searchAnchorRef.current}
        placement="bottom-start"
        style={{
          zIndex: 1300,
          width: searchAnchorRef.current?.clientWidth,
        }}
      >
        <ClickAwayListener
          onClickAway={(event) => {
            // A click inside a floating menu / popover (a result row's "more
            // options" menu is portaled to the body, outside this dropdown) is
            // not an outside click — ignore it so opening a row's menu does not
            // collapse the search dropdown out from under it.
            const target = event.target as HTMLElement | null;
            if (target?.closest?.(".MuiPopover-root")) return;
            setSearchOpen(false);
          }}
        >
          <Paper
            elevation={4}
            sx={{
              mt: 0.5,
              maxHeight: 360,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {searchResults.length === 0 ? (
              <EmptyState size="compact" title="No matches" />
            ) : (
              <List dense disablePadding>
                {searchResults.map((item) => {
                  const isFolder = item.type === strings.folder;
                  return (
                    <SearchResultRow
                      key={
                        isFolder
                          ? `f-${item.autoGeneratedId}`
                          : `r-${item.id ?? item.autoGeneratedId}`
                      }
                      isFolder={isFolder}
                      title={
                        isFolder
                          ? (item.templateName ?? "")
                          : (item.emailSubject ?? "")
                      }
                      crumbs={resolveAncestorCrumbs(item)}
                      createdBy={item.requestedBy?.name}
                      createdOn={item.createdOn ?? item.createdAt}
                      onClick={() => onSearchSelect(item)}
                      actions={renderItemActions?.(item)}
                      badge={renderItemBadge?.(item)}
                    />
                  );
                })}
              </List>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      <Box sx={styles.dateField}>
        <CustomDateRangePicker
          dateRange={dateRange}
          openDateRangePicker={openDateRangePicker}
          setOpenDateRangePicker={setOpenDateRangePicker}
          setDateRange={setDateRange}
          helperFunction={onDateApplied}
          disabled={disabled}
          isClearable={false}
          autoSizeInput
          sx={styles.datePickerTrigger}
          startIcon={
            <>
              {/*
              Both marks are in the DOM and the breakpoint picks one: the
              narrow phone column draws the filter glyph alone, every wider
              column the calendar in front of the value.
            */}
              <FilterLinesGlyph sx={styles.dateGlyphCompact} />
              <CalendarGlyph sx={styles.dateGlyph} />
            </>
          }
          endIcon={<CaretDownGlyph sx={styles.dateCaret} />}
        />
      </Box>
    </Box>
  );
};

export default SignRequestsToolbar;
